import { useAtom } from 'jotai';
import { userAtom, accessTokenAtom } from '../../../atoms';
import { Navigate } from 'react-router-dom';
import { paths } from '../../../../config/paths';
import { useState } from 'react';
import type { FixReferralRequest, FixReferralResponse } from '@api/v1/admin/referral-fixer/type';

export const FixReferralsPage = () => {
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [formData, setFormData] = useState<FixReferralRequest>({
        parentEmail: '',
        childEmail: '',
        linkType: 'referral'
    });

    if (!user?.has_admin_role) {
        return <Navigate to={paths.dashboard.path} replace />;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!accessToken) {
            setError('認証情報が見つかりません');
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch('/api/v1/admin/referral-fixer', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json() as FixReferralResponse;

            if (!response.ok) {
                throw new Error(result.message || '紹介関係の修正に失敗しました');
            }

            setSuccess(result.message);
            setFormData({
                parentEmail: '',
                childEmail: '',
                linkType: 'referral'
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-2">紹介関係の修正</h1>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-yellow-700">
                    これは紹介コードの入力を忘れた子ユーザー向けの修正ツールです。それ以外のケースは管理者に連絡してください。
                </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
                {error && (
                    <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-4">
                        {success}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="parentEmail" className="block text-sm font-medium text-gray-700 mb-1">
                            親ユーザーのメールアドレス
                        </label>
                        <input
                            type="email"
                            id="parentEmail"
                            name="parentEmail"
                            value={formData.parentEmail}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="childEmail" className="block text-sm font-medium text-gray-700 mb-1">
                            子ユーザーのメールアドレス
                        </label>
                        <input
                            type="email"
                            id="childEmail"
                            name="childEmail"
                            value={formData.childEmail}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="linkType" className="block text-sm font-medium text-gray-700 mb-1">
                            リンクタイプ
                        </label>
                        <select
                            id="linkType"
                            name="linkType"
                            value={formData.linkType}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="referral">直紹介リンク経由</option>
                            <option value="distributor_code">代理店リンク経由</option>
                        </select>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full px-4 py-2 rounded-md ${isLoading
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-indigo-600 hover:bg-indigo-700'
                            } text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                    >
                        {isLoading ? '処理中...' : '修正する'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default FixReferralsPage;